import { Box, Button, Flex, FormControl, FormLabel, GridItem, Input, Select, SimpleGrid, Text, Icon, useColorModeValue } from "@chakra-ui/react";
import ReportsApi from "Api/ReportsApi";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLoading } from "Providers/LoadingProvider";
import { Field, Form, Formik } from "formik";
import StationsApi from "Api/StationsApi";
import { FaSearch } from "react-icons/fa";
import PrintMode from "components/Buttons/PrintMode";
import Favorites from "Helpers/Favorites";
import ReactApexChart from "react-apexcharts";
import jsonData from "../../../apex_chart_pt_br.json";
import { windDirecitonArrow } from "views/Components/Forecast16Graphs";
import { TbChartLine, TbDownload } from "react-icons/tb";


function DetailedReport() {
    // const [data, setData] = useState(null);
    const loading = useLoading();
    const [currentValues, setCurrentValues] = useState(null);

    async function loadData(station_id, type, from, to) {
        setCurrentValues(null);
        loading.show();

        try {
            let response = await ReportsApi.detailed(station_id, type, from, to);

            setCurrentValues({
                station_id,
                type,
                from,
                to,
                json: response.json,
            });
        } finally {
            loading.hide();
        }
    }

    const data = currentValues?.json || null;

    const exportData = useCallback(() => {
        const isDaily = false;// currentValues?.type == '24h' || currentValues?.type == 'day';

        const headers = isDaily ?
        {
            'period': 'Momento',
            'temp': 'Temperatura',
            'humidity': 'Umidade',
            'pressure': 'Pressão Atmosférica',
            'wind_speed': 'Velocidade do Vento',
            'wind_dir': 'Direção do Vento',
            'wind_gust': 'Rajada de Vento',
            'precip_rate': 'Índice de Precipitação',
            'precip_total': 'Precipitação Acumulada',
            'solar_radiation': 'Radiação Solar',
            'uv': 'Índice UV',
        } : {
            'period': 'Momento',
            'temp_min': 'Temperatura Mínima',
            'temp_avg': 'Temperatura Média',
            'temp_max': 'Temperatura Máxima',
            'humidity_min': 'Umidade Mínima',
            'humidity_avg': 'Umidade Média',
            'humidity_max': 'Umidade Máxima',
            'pressure_min': 'Pressão Mínima',
            'pressure_avg': 'Pressão Média',
            'pressure_max': 'Pressão Máxima',
            'wind_speed_max': 'Velocidade do Vento Máxima',
            'wind_dir_avg': 'Direção do Vento Média',
            'wind_gust_max': 'Rajada de Vento Máxima',
            'precip_total': 'Precipitação Total',
            'solar_radiation_max': 'Radiação Solar Máxima',
            'uv_max': 'Índice UV Máximo',
        };

        // Export CSV including headers and then include all rows, file name should be current_filter.station_id + _ + current_filter.type + relatorio detalhadao .csv
        let csvContent = "data:text/csv;charset=utf-8," +
            Object.values(headers).join(",") + "\n" +
            data.data.map(e => {
                return Object.keys(headers).map(k => {
                    let v = e[k];
                    if (v == null) return '';
                    if (k == 'period') return `"${new Date(v).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}"`; // datetime in brazil timezone
                    if (typeof v === 'string' && v.indexOf(',') != -1) return `"${v.replace(/"/g, '""')}"`; // escape quotes
                    return v;
                }).join(",");
            }).join("\n");

        var encodedUri = encodeURI(csvContent);
        var link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `${currentValues.station_id}_${currentValues.type}_relatorio_detalhado.csv`);
        document.body.appendChild(link); // Required for FF

        link.click(); // This will download the data file named "my_data.csv".

        document.body.removeChild(link);
    }, [currentValues]);

        
    const isDaily = false;// currentValues?.type == '24h' || currentValues?.type == 'day';
    // Yearly or custom with more than 30 days
    const isYearly = currentValues?.type == 'year' || (currentValues?.type == 'custom' && currentValues?.from && currentValues?.to && (new Date(currentValues.to) - new Date(currentValues.from)) > 30 * 24 * 60 * 60 * 1000);

    const Charts = useMemo(() => <ReportCharts isDaily={isDaily} isYearly={isYearly} data={data?.data} extraFields={data?.extra_fields} />, [currentValues]);

    const cardBg = useColorModeValue("white", "gray.800");
    const borderColor = useColorModeValue("gray.200", "gray.700");

    return (
        <Box
            bg={cardBg}
            borderRadius="xl"
            overflow="hidden"
            boxShadow="md"
            border="1px solid"
            borderColor={borderColor}
            overflowX="auto"
        >
            <Box
                bg="linear-gradient(135deg, #2D6B3F 0%, #1F5A2F 100%)"
                py={5}
                px={6}
            >
                <Flex align="center" gap={3}>
                    <Flex align="center" justify="center" bg="whiteAlpha.200" borderRadius="lg" p={2}>
                        <Icon as={TbChartLine} color="white" boxSize={5} />
                    </Flex>
                    <Box>
                        <Text color="white" fontWeight="bold" fontSize="xl">Relatório Detalhado</Text>
                        <Text color="whiteAlpha.800" fontSize="sm">Análise completa de dados meteorológicos</Text>
                    </Box>
                </Flex>
            </Box>
            <Box p={6}>
                <Flex direction="column" w="100%">
                    <Box mb={4}>
                        <FormFilter loadData={loadData} />
                    </Box>

                    {Charts}

                    {data?.data == null && <Text fontStyle={"italic"} textAlign={"center"} w="100%" color="gray.500">Nenhum dado a ser exibido.</Text>}

                    {
                        data?.provider == 'INMET' &&
                        <Box mb={2} w="100%" textAlign={"center"} fontSize="sm">
                            Fonte de dados: INMET
                        </Box>
                    }
                    <PrintMode />

                    {data?.data != null && <Flex mt={2} justify={"center"} w={"full"}>
                        <Button
                            size="sm"
                            variant="outline"
                            borderColor="brand.500"
                            color="brand.500"
                            _hover={{ bg: "brand.50" }}
                            borderRadius="xl"
                            leftIcon={<TbDownload />}
                            onClick={exportData}
                        >
                            Exportar para CSV
                        </Button>

                    </Flex>}
                </Flex>
            </Box>
        </Box>
    );
}

function FormFilter({ loadData }) {
    const [stations, setStations] = useState([]);
    const loading = useLoading();
    const favorites = Favorites.get();
    const initialStationId = new URLSearchParams(window.location.search).get('station');


    useEffect(() => {
        loadStationOptions();
    }, []);

    async function loadStationOptions() {
        loading.show();
        try {
            let response = await StationsApi.options();
            setStations(response.json);
        } finally {
            loading.hide();
        }
    }

    async function submitHandle(values) {
        loadData(values.station_id, values.type, values.from || null, values.to || null);
    }

    const filterBg = useColorModeValue("gray.50", "gray.700");

    return (
        <Formik onSubmit={submitHandle} initialValues={{ station_id: initialStationId || null, type: 'month', from: null, to: null }} enableReinitialize={true}>
            {props => <>
                <Form id="reportFilterForm">
                    <Box bg={filterBg} p={4} borderRadius="xl">
                        <Flex flexDirection="row" flexWrap w="100%">
                            <SimpleGrid w="100%" columns={{ sm: 1, lg: 12 }} gap={4}>
                            <GridItem colSpan={{
                                base: 12,
                                lg: ["7days", "30days", "24h"].indexOf(props.values.type) != -1 ? 8 : // sem campo de data
                                    props.values.type != "custom" ? 6 // 1 campo de data
                                        : 4 // Padrao, dois campos de data
                            }}>
                                <Field name="station_id">
                                    {({ field, form }) => (
                                        <FormControl isRequired>
                                            <FormLabel mb="0" htmlFor="station_id" fontWeight="normal">Estação:</FormLabel>
                                            <Select id="station_id" {...field} borderRadius="xl" fontSize="md" placeholder="Selecione a Estação...">
                                                {
                                                    !favorites || !favorites.length ?
                                                        (
                                                            stations.map((station, k) => (
                                                                <option key={k} value={station.id}>{station.text}</option>
                                                            ))
                                                        )
                                                        :
                                                        (
                                                            <>
                                                                <optgroup key="favorites" label="Estações Favoritas">
                                                                    {stations.map((station, k) => {
                                                                        if (favorites.includes(station.identifier))
                                                                            return (<option key={k} value={station.id}>{station.text}</option>);
                                                                        else
                                                                            return "";
                                                                    })}
                                                                </optgroup>
                                                                <optgroup key="others" label="Outras Estações">
                                                                    {stations.map((station, k) => {
                                                                        if (!favorites.includes(station.identifier))
                                                                            return (<option key={k} value={station.id}>{station.text}</option>);
                                                                        else
                                                                            return "";
                                                                    })}
                                                                </optgroup>
                                                            </>
                                                        )
                                                }
                                            </Select>
                                        </FormControl>
                                    )}
                                </Field>
                            </GridItem>

                            <GridItem colSpan={{ base: 12, lg: 2 }}>
                                <Field name="type">
                                    {({ field }) => (
                                        <FormControl isRequired>
                                            <FormLabel mb="0" htmlFor="type" fontWeight="normal">Período:</FormLabel>
                                            <Select id="type" {...field} borderRadius="xl" fontSize="md" placeholder="Selecione...">
                                                <option value="24h">Últimas 24h</option>
                                                <option value="day">Diário</option>
                                                <option value="week">Semana</option>
                                                <option value="7days">Últimos 7 dias</option>
                                                <option value="month">Mensal</option>
                                                <option value="30days">Últimos 30 dias</option>
                                                <option value="year">Anual</option>
                                                <option value="custom">Customizado</option>
                                            </Select>
                                        </FormControl>
                                    )}
                                </Field>
                            </GridItem>

                            {
                                ["7days", "30days", "24h"].indexOf(props.values.type) == -1 && <>
                                    <GridItem colSpan={{ sm: 12, lg: 2 }}>
                                        <Field name="from">
                                            {({ field, form }) => (
                                                <FormControl isRequired>
                                                    <FormLabel mb="0" htmlFor="from" fontWeight="normal">Data:</FormLabel>
                                                    <Input id="from" {...field} borderRadius="xl" fontSize="md" type={
                                                        props.values.type == "year" ? "number" :
                                                            props.values.type == "month" ? "month" :
                                                                props.values.type == "week" ? "week" :
                                                                    "date"
                                                    } min={props.values.type == "year" ? 2000 : null} placeholder={props.values.type == "year" ? (new Date).getFullYear() : ""} />
                                                </FormControl>
                                            )}
                                        </Field>
                                    </GridItem>
                                </>}

                            {
                                props.values.type == "custom" && <>
                                    <GridItem colSpan={{ sm: 12, lg: 2 }}>
                                        <Field name="to">
                                            {({ field, form }) => (
                                                <FormControl isRequired>
                                                    <FormLabel mb="0" htmlFor="to" fontWeight="normal">até:</FormLabel>
                                                    <Input id="to" {...field} borderRadius="xl" fontSize="md" type="date" />
                                                </FormControl>
                                            )}
                                        </Field>
                                    </GridItem>
                                </>
                            }

                            <GridItem colSpan={{ sm: 12, lg: 2 }}>
                                <Button w="100%" type='submit' form='reportFilterForm' bg="brand.500" color="white" _hover={{ bg: "brand.600" }} borderRadius="xl" mt={{ lg: 6 }} leftIcon={<FaSearch />}>
                                    Buscar
                                </Button>
                            </GridItem>
                        </SimpleGrid>
                    </Flex>
                    </Box>
                </Form>
            </>}
        </Formik>
    );
}

function ReportCharts({ data, isDaily, isYearly, extraFields }) {
    if (!data) return <></>;

    const getChartOptions = (id, suffix = "", dataLabels = true, customDatalabels = null, type, hasRangeArea, forceDatalabelsIndex = null, min, max) => {
        return {
            chart: {
                id: id,
                type: type || 'line',
                zoom: { enabled: false },
                toolbar: {
                    show: false,
                },
                events: {
                    mouseMove: function (e, ctx, config) {
                        window.__activeApexChartId = config.globals.chartID;
                    },
                    mouseLeave: function () {
                        window.__activeApexChartId = null;
                    }
                },
                group: "detailed-data",
                locales: [jsonData],
                defaultLocale: "pt-br",
            },
            stroke: { width: [2, 3] },
            tooltip: {
                custom: function ({ seriesIndex, dataPointIndex, w }) {
                    // show ONLY on the hovered chart
                    if (window.__activeApexChartId && window.__activeApexChartId !== w.globals.chartID) {
                        return '<div style="display:none"></div>'; // hide on synced siblings
                    }

                    const hoveredX = w.globals.seriesX[seriesIndex][dataPointIndex];
                    const dateStr = new Date(hoveredX).toLocaleString('pt-BR', isYearly ?
                        { day: '2-digit', month: 'long', year: 'numeric' } :
                        {day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit'}
                    );

                    // helper to get real color
                    const getSeriesColor = (i) =>
                        (w.globals.stroke?.colors?.[i]) ||
                        (w.globals.colors?.[i]) ||
                        (w.globals.fill?.colors?.[i]) ||
                        (w.config.series?.[i]?.color) ||
                        (w.config.colors?.[i]) || '#999';

                    const rows = w.config.series.map((s, i) => {
                        const arr = s.data || [];
                        const idx = dataPointIndex;

                        let valHtml = '—';
                        if (idx !== -1) {
                            const pt = arr[idx];
                            if (pt?.y != null) {
                                valHtml = Array.isArray(pt.y) ? `${pt.y[0]}${suffix} – ${pt.y[1]}${suffix}` : `${pt.y}${suffix}`;
                            } else {
                                valHtml = `${pt}${suffix}`;
                            }
                        }

                        const color = getSeriesColor(i);
                        const name = s.name || `Série ${i + 1}`;

                        return `
                            <div style="display:flex;align-items:center;justify-content:space-between;margin:2px 0;gap:8px;">
                                <div style="display:flex;align-items:center;gap:8px;">
                                <span style="width:8px;height:8px;border-radius:50%;background:${color};
                                            display:inline-block;border:1px solid rgba(0,0,0,.25)"></span>
                                <span>${name}</span>
                                </div>
                                <span style="font-weight:600;">${valHtml}</span>
                            </div>
                            `;
                    }).join('');

                    return `
                        <div style="background:#1f2937;color:#fff;padding:8px 10px;border-radius:8px;min-width:220px;">
                        <div style="font-size:12px;opacity:.85;margin-bottom:6px;">${dateStr}</div>
                        ${rows}
                        </div>
                    `;
                },
                theme: "dark",
                shared: true,
                intersect: false,
                x: {
                    show: true,
                    format: isYearly ? "dd MMM" : "dd MMM HH:mm",
                },
            },
            dataLabels: {
                enabled: dataLabels,
                enabledOnSeries: forceDatalabelsIndex ? forceDatalabelsIndex : (isDaily || !hasRangeArea ? [0] : [1]),
                formatter: function (val, opts) {
                    if (customDatalabels != null) {
                        return customDatalabels(opts.seriesIndex, opts.dataPointIndex);
                    }

                    // Show only up to 20 labels
                    let step = Math.ceil(data.length / 20);

                    if (opts.dataPointIndex == 0 || opts.dataPointIndex % step != 0) {
                        return "";
                    }

                    if (val == null) return "";

                    return val + suffix;
                },
            },
            xaxis: {
                type: "datetime",
                categories: data?.map((d) => d.period) || [],
                show: true,
                color: "#A0AEC0",
                labels: {
                    show: true,
                    datetimeUTC: false,
                    style: {
                        colors: "#A0AEC0",
                        fontSize: "14px",
                    },
                },
            },
            yaxis: {
                show: true,
                color: "#A0AEC0",
                min: min,
                max: max,
                labels: {
                    show: true,
                    style: {
                        colors: "#A0AEC0",
                        fontSize: "14px",
                    },
                },
            },
        };
    };

    function existsSeriesData(dataKey) {
        // check if there is any non-null value
        return data?.filter((d) => d[dataKey] != null)?.length > 0;
    }

    function QuickChart({ label, suffix, id, type, series, dataLabels, customDatalabels = null, hasRangeArea = true, forceDatalabelsIndex = null, min = undefined, max = undefined }) {
        return <Flex direction="column" mb={4}>
            <Text fontSize={"lg"} fontWeight={"semibold"} textAlign={"center"}>
                {label}
            </Text>
            <ReactApexChart
                type={type || "line"}
                options={getChartOptions(id, suffix, dataLabels, customDatalabels, type, hasRangeArea, forceDatalabelsIndex, min, max)}
                series={series}
                width="100%"
                height={250}
            />
        </Flex>
    }

    return <Flex direction={"column"} gap={8}>

        {existsSeriesData(isDaily ? "temp" : "temp_avg") &&
            <QuickChart label="Temperatura (°C)" type={isDaily ? "line" : "rangeArea"} id="temp" color="#ECC94B" suffix="°" series={
                isDaily ? [
                    {
                        name: "Temperatura (°C)",
                        data: data?.map((d) => parseFloat(d.temp)) || [],
                        color: "#ECC94B",
                    },
                ] : [
                    {
                        name: "Temperatura (°C)",
                        data: data?.map((d) => ({ x: d.period, y: [d.temp_min, d.temp_max] })) || [],
                        color: "#3182CE60",
                        type: "rangeArea",
                    },
                    {
                        name: "Temperatura Média (°C)",
                        data: data?.map((d) => ({ x: d.period, y: d.temp_avg })) || [],
                        color: "#f97316",
                        type: "line",
                    },
                ]} />
        }

        {existsSeriesData(isDaily ? "humidity" : "humidity_avg") && <QuickChart label="Umidade (%)" suffix="%" type={isDaily ? "line" : "rangeArea"} id="humidity" color="#3182CE" series={
            isDaily ? [
                {
                    name: "Umidade (%)",
                    data: data?.map((d) => parseFloat(d.humidity)) || [],
                    color: "#3182CE",
                },
            ] : [
                {
                    type: "rangeArea",
                    name: "Umidade (%)",
                    data: data?.map((d) => ({ x: d.period, y: [d.humidity_min, d.humidity_max] })) || [],
                    color: "#3182CE50",

                },
                {
                    name: "Umidade Média (%)",
                    data: data?.map((d) => ({ x: d.period, y: d.humidity_avg })) || [],
                    color: "#3182CE",
                    type: "line",
                },
            ]} />
        }



        {existsSeriesData(isDaily ? "solar_radiation" : "solar_radiation_max") && <QuickChart label="Radiação Solar (W/m²)" suffix=" W/m²" type="area" id="solar_radiation" color="#F56565" forceDatalabelsIndex={[0]} series={[
            {
                name: "Radiação Solar (W/m²)",
                data: data?.map((d) => d.solar_radiation ?? d.solar_radiation_max) || [],
                color: "#F56565",
            }
        ]} />}

        {existsSeriesData(isDaily ? "uv" : "uv_max") && <QuickChart label="Índice UV" id="uv" color="#f97316" type="area" forceDatalabelsIndex={[0]} series={[
            {
                name: "Índice UV",
                data: data?.map((d) => d.uv ?? d.uv_max) || [],
                color: "#f97316",
            },
        ]} />}

        {existsSeriesData(isDaily ? "wind_speed" : "wind_speed_max") &&
            <QuickChart label="Vento (km/h)" suffix=" km/h" id="wind_speed" color="#48BB78" dataLabels={true} hasRangeArea={false} series={[
                {
                    name: `Vento ${isDaily ? "" : "Máximo"} (km/h)`,
                    data: data?.map((d) => d.wind_speed ?? d.wind_speed_max) || [],
                    color: "#48BB78",
                    zIndex: 2
                },
                {
                    name: "Rajada Máxima (km/h)",
                    data: data?.map((d) => d.wind_gust ?? d.wind_gust_max) || [],
                    color: "#3182CE40",
                    type: "area",
                    zIndex: 1
                }
            ]} customDatalabels={(seriesIndex, dataIndex) => {
                if (seriesIndex != 0) return "";

                let step = Math.ceil(data.length / 10);

                if (dataIndex == 0 || dataIndex % step != 0)
                    return "";


                return (data[dataIndex]?.wind_speed ?? data[dataIndex]?.wind_speed_max) + " km/h  " + windDirecitonArrow(data[dataIndex]?.wind_dir ?? data[dataIndex]?.wind_dir_avg) + " " + (data[dataIndex]?.wind_dir ?? data[dataIndex]?.wind_dir_avg) + "°";
            }} />
        }

        {existsSeriesData("precip_total") && <QuickChart label="Chuva (mm)" suffix=" mm" id="rain" forceDatalabelsIndex={[0]} color="#4299E1" series={[

            {
                name: "Chuva Acumulada (mm)",
                data: data?.map((d) => parseFloat(d.precip_total)) || [],
                color: "#75afec",
                type: "area",
                stroke: {
                    opacity: 0.5
                }
            },
            {
                name: "Chuva (mm)",
                data: data?.map((d) => parseFloat(d.precip_rate)) || [],
                color: "#3182CE",
                type: "column",
            },
        ]} />
        }

        {existsSeriesData(isDaily ? "pressure" : "pressure_avg") &&
            <QuickChart label="Pressão (hPa)" suffix=" hPa" id="pressure" color="#ED64A6" type={isDaily ? "line" : "rangeArea"} min={Math.min(...(data?.map((d) => d.pressure ?? d.pressure_min  )))} max={Math.max(...(data?.map((d) => d.pressure ?? d.pressure_max  )))} series={
                isDaily ? [
                    {
                        name: "Pressão (hPa)",
                        data: data?.map((d) => parseFloat(d.pressure)) || [],
                        color: "#ED64A6",
                    },
                ] : [
                    {
                        type: "rangeArea",
                        name: "Pressão (hPa)",
                        data: data?.map((d) => ({ x: d.period, y: [d.pressure_min, d.pressure_max] })) || [],
                        color: "#ED64A650",

                    },
                    {
                        name: "Pressão Média (hPa)",
                        data: data?.map((d) => ({ x: d.period, y: d.pressure_avg })) || [],
                        color: "#ED64A6",
                        type: "line",
                    },
                ]} />
        }

        {extraFields?.length > 0 && extraFields.map(fieldData => (
            <QuickChart
                key={`extra-${fieldData.field_id}`}
                label={`${fieldData.name}${fieldData.unit ? ` (${fieldData.unit})` : ''}`}
                suffix={fieldData.unit ? ` ${fieldData.unit}` : ''}
                id={`extra-${fieldData.field_id}`}
                type="rangeArea"
                hasRangeArea={true}
                series={[
                    {
                        type: "rangeArea",
                        name: fieldData.name,
                        data: fieldData.data?.map((d) => ({ x: d.period, y: [d.min_value, d.max_value] })) || [],
                        color: "#2D6B3F50",
                    },
                    {
                        name: `${fieldData.name} Média`,
                        data: fieldData.data?.map((d) => ({ x: d.period, y: d.avg_value })) || [],
                        color: "#2D6B3F",
                        type: "line",
                    },
                ]}
            />
        ))}

    </Flex>
}

export default DetailedReport;
